import * as QueryString from 'query-string'
import zionDebug from '@fs/zion-debug'
import { i18n } from '@fs/zion-locale'
import {
  SEARCH_PARAMS,
  SEARCH_WITHIN_FILTER_PARAMS,
  SEARCH_DIRECTLY_WITHIN_FILTER_PARAMS,
  SEARCH_BY_LOCATION_PARAMS,
} from './searchParams'
import {
  getInternalResultsClientFilterFn,
  SEARCH_FILTER_TYPES,
  SEARCH_TYPE,
  uriDecodeCheck,
} from '../SearchFilter/searchFilterHelper'

const EMPTY_FILTER_LENGTH = 3

const debug = zionDebug('standards:places:searchHelper')

export function getInputTextFromSearchWithin(searchWithin) {
  return `${i18n.t('places-app.search-within', 'Search Within')}:${searchWithin.primaryText}`
}

export function cleanInputText(inputText) {
  const searchWithinText = i18n.t('places-app.search-within', 'Search Within')
  return inputText ? inputText.replace(`${searchWithinText}:`, '') : inputText
}

export function parseCriteriaFromInputText({ text, radius = 2, unit = 'k' }) {
  let type = 'SEARCH_BY_TEXT'
  const criteria = {}
  if (text?.length <= 0) return { type, criteria }

  const { lat, lng } = parseLocationFromTextInput(text)
  if (lat && lng) {
    // Distance Lat,Lng Search
    criteria.lat = lat
    criteria.lng = lng
    criteria.distance = `${radius}${unit}`
    type = 'SEARCH_BY_LOCATION'
  } else {
    // Text Name Search
    criteria.text = text
  }

  return { type, criteria }
}

function getDecimalCount(num) {
  if (Number.isNaN(num) || Math.floor(Number(num)) === Number(num)) {
    return 0
  }
  return String(num).split('.')[1].length || 0
}

function getFixedNumberValue(value, maxDecimals = 2) {
  const floatValue = parseFloat(value)
  const valueFixed = getDecimalCount(floatValue) > maxDecimals ? floatValue.toFixed(maxDecimals) : floatValue
  return !Number.isNaN(valueFixed) ? valueFixed : undefined
}

function getDistanceValue(distance) {
  return getFixedNumberValue(distance, 2)
}

function getLatLngValue(latLng) {
  return getFixedNumberValue(latLng, 4)
}

export function getDistanceValueAndUnitFromSearchByLocationParams(params) {
  const { lat, lng, distance } = params || {}
  const latitude = getLatLngValue(lat)
  const longitude = getLatLngValue(lng)
  const distanceValue = getDistanceValue(distance)
  const lastDistanceChar = distance ? distance[distance.length - 1] : 'k'
  const distanceType = lastDistanceChar === 'm' ? 'm' : 'k'
  return { latitude, longitude, distanceValue, distanceType }
}

export function getInputTextFromSearchByLocationParams(params) {
  const { lat, lng } = params
  if (lat && lng) {
    const { latitude, longitude, distanceValue, distanceType } =
      getDistanceValueAndUnitFromSearchByLocationParams(params)
    if (distanceValue && distanceType) {
      return `${latitude},${longitude}`
    }
    return `${lat}, ${lng}`
  }
  return undefined
}

export function getSearchWithinParams(params) {
  const searchWithinParams = {}
  Object.keys(params).forEach((key) => {
    if (SEARCH_WITHIN_FILTER_PARAMS[key]) {
      searchWithinParams[key] = params[key]
    }
    if (SEARCH_DIRECTLY_WITHIN_FILTER_PARAMS[key]) {
      searchWithinParams[key] = params[key]
    }
  })
  return searchWithinParams
}

export function getJurisdictionChain(jurisdiction, arr = []) {
  arr.push(jurisdiction.id)
  if (!jurisdiction.jurisdiction) return arr
  return getJurisdictionChain(jurisdiction.jurisdiction, arr)
}

export function childReplaceParent(filters, jurisdictionChain) {
  return filters?.filter((filter) => !filter.reqParents || !jurisdictionChain?.includes(filter.reqParents))
}

export function updatePlacesURLForSelectedText({ history, text, radius, unit, method = 'push' }) {
  const { type, criteria } = parseCriteriaFromInputText({ text, radius, unit })

  switch (type) {
    case 'SEARCH_BY_TEXT':
    case 'SEARCH_WITHIN':
      {
        const deleteParams = {
          ...SEARCH_DIRECTLY_WITHIN_FILTER_PARAMS,
          ...SEARCH_WITHIN_FILTER_PARAMS,
          ...SEARCH_BY_LOCATION_PARAMS,
          focusedId: '',
          mapShowCircle: true,
          searchTypeaheadInputText: text,
          pagenum: 1,
        }
        const deleteHashParams = {
          ...SEARCH_DIRECTLY_WITHIN_FILTER_PARAMS,
          ...SEARCH_WITHIN_FILTER_PARAMS,
        }
        const cleanText = cleanInputText(text).replace(/^ +|^( )$/g, '')
        if (cleanText.length > 0) {
          updatePlacesUrl({ history, addParams: { text: cleanText }, deleteParams, deleteHashParams, method })
        } else {
          updatePlacesUrl({ history, addParams: {}, deleteParams: { ...deleteParams, text: '' }, deleteHashParams })
        }
      }
      break
    case 'SEARCH_BY_LOCATION': {
      const criteriaCopy = { ...criteria }
      delete criteriaCopy.focusedId
      updatePlacesUrl({
        history,
        addParams: { ...criteriaCopy, searchTypeaheadInputText: text, mapShowCircle: true },
        deleteAll: true,
      })
      break
    }
    default:
      throw new Error('Unknown search type in updatePlacesURLForSelectedText !!!')
  }
}

function getInputTextFromSearchWithinParams(params) {
  const { reqParents, reqDirParents, primaryText } = params
  if (reqParents || reqDirParents) {
    const parentPlace = primaryText || reqDirParents || reqParents
    return `${i18n.t('places-app.search-within', 'Search Within')}: ${parentPlace}`
  }
  return undefined
}

export function getSearchTypeaheadInputTextFromParams(params, state) {
  const searchTypeaheadInputText = params.searchTypeaheadInputText
  if (searchTypeaheadInputText) {
    return searchTypeaheadInputText
  }

  const searchByLocationInputText = getInputTextFromSearchByLocationParams(params)
  if (searchByLocationInputText) {
    return searchByLocationInputText
  }

  const searchWithinInputText = getInputTextFromSearchWithinParams(params, state)
  if (searchWithinInputText) {
    return searchWithinInputText
  }

  const text = params.text
  if (text) {
    return text
  }

  return ''
}

function isValidSearchCriteriaParam(params, key) {
  switch (key) {
    case 'distance':
      {
        const valueString = params[key]
        if (valueString.length < 2) {
          return false
        }
        const measurementType = valueString[valueString.length - 1]
        if (measurementType !== 'm' && measurementType !== 'k') {
          return false
        }

        const distanceString = valueString.slice(0, valueString.length - 1)
        if (Number.isNaN(Number(distanceString))) {
          return false
        }
      }
      break
    case 'lat':
      if (Number.isNaN(Number(params[key]))) {
        return false
      }
      break
    case 'lng':
      if (Number.isNaN(Number(params[key]))) {
        return false
      }
      break
    default:
      break
  }
  return true
}

export function getSearchCriteriaFromParams(params) {
  const searchCriteria = {}
  Object.keys(params).forEach((key) => {
    if (SEARCH_PARAMS[key]) {
      if (isValidSearchCriteriaParam(params, key)) {
        searchCriteria[key] = params[key]
      }
    }
  })
  if (Object.keys(searchCriteria.length > 0)) {
    return searchCriteria
  }
  return null
}

function getSearchWithinCriteriaFromFilters(filters) {
  return filters.reduce((parents, filter) => {
    if (filter.type !== SEARCH_FILTER_TYPES.SEARCH_WITHIN) return parents

    const key = filter.directChildrenOnly ? 'reqDirParents' : 'reqParents'
    const newReqParents = filter.reqParents.toString()

    return {
      ...parents,
      [key]: parents[key] ? `${parents[key]},${newReqParents}` : newReqParents,
    }
  }, {})
}

function getSearchTypeFromParamsAndFilters({ filters = [], urlQueryParams = {} }) {
  const { lat, lng, distance } = urlQueryParams
  if (lat && lng && distance) {
    return SEARCH_TYPE.LOCATION
  }

  const searchWithinFilter = filters.find((f) => f.type === SEARCH_FILTER_TYPES.SEARCH_WITHIN)
  if (searchWithinFilter) {
    return SEARCH_TYPE.SEARCH_WITHIN
  }

  return SEARCH_TYPE.TEXT
}

const PLACE_TYPE_GROUPS = {
  26: 'Countries',
  27: 'States and Provinces',
  28: 'Counties and Districts',
  8: 'Cities and Towns',
  3: 'Churches and Church Parishes',
}

function getExactFilterFromFilters(filters) {
  return filters.find((f) => f.type === SEARCH_FILTER_TYPES.EXACT)
}

function getPlaceTypeCriteriaFromFilters(filters) {
  const reqTypes = []
  const reqTypeGroups = []

  filters.forEach((filter) => {
    if (filter.type === SEARCH_FILTER_TYPES.TYPE && filter?.placeType?.id) {
      if (filter?.placeType?.key?.match(/-placeType\b/)) {
        reqTypes.push(filter.placeType.id)
      }
      if (filter?.placeType?.key?.match(/-groupType\b/) && PLACE_TYPE_GROUPS[filter.placeType.id]) {
        reqTypeGroups.push(filter.placeType.id)
      }
    }
  })

  return { reqTypes: reqTypes.join() || undefined, reqTypeGroups: reqTypeGroups.join() || undefined }
}

function getDateCriteriaFromFilters(filters) {
  const dateFilters = filters.filter((f) => f.type === SEARCH_FILTER_TYPES.DATE)
  return dateFilters.reduce((reqYears, filter) => {
    if (!reqYears) {
      return filter.reqYears
    }
    return `${reqYears},${filter.reqYears}`
  }, undefined)
}

export function getCertificationLevelCriteriaFromFilters(filters) {
  const certificationLevelFilters = filters.filter((f) => f.type === SEARCH_FILTER_TYPES.CERTIFICATION)
  const { accepted, certified, provisional } = certificationLevelFilters.reduce((certificationLevels, filter) => {
    if (filter.certificationLevel === 'accepted') {
      certificationLevels.accepted = true
    }
    if (filter.certificationLevel === 'certified') {
      certificationLevels.certified = true
    }

    if (filter.certificationLevel === 'provisional') {
      certificationLevels.provisional = true
    }
    return certificationLevels
  }, {})

  if (!accepted && !certified && provisional) {
    return { valType: 'non_val_only', pubType: 'non_pub_only' }
  }

  if (accepted && !certified && !provisional) {
    return { valType: 'non_val_only', pubType: 'pub_only' }
  }

  if (!accepted && certified && !provisional) {
    return { valType: 'val_only', pubType: 'pub_only' }
  }

  if (provisional && accepted && !certified) {
    return { valType: 'non_val_only' }
  }

  // Place API doesn't have a way to filter !accepted, so we do it on the client
  if (provisional && !accepted && certified) {
    return { certificationLevel: 'not-accepted' }
  }

  if (!provisional && accepted && certified) {
    return { pubType: 'pub_only' }
  }

  return {}
}

function getCommonSearchCriteriaFromParams({ pagenum, pagesize, exact, limit }) {
  const criteria = { pagenum, pagesize, exact, limit }
  Object.keys(criteria).forEach((key) => (criteria[key] === undefined ? delete criteria[key] : {}))
  return criteria
}

function removeUndefinedProperties(obj) {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key] = value
    }
    return acc
  }, {})
}

function getSearchWithinSearchCriteriaFromFilters(filters) {
  const { reqParents, reqDirParents } = getSearchWithinCriteriaFromFilters(filters)
  const { reqTypes, reqTypeGroups } = getPlaceTypeCriteriaFromFilters(filters)
  const reqYears = getDateCriteriaFromFilters(filters)
  const { valType, pubType } = getCertificationLevelCriteriaFromFilters(filters)
  const criteria = {
    reqParents,
    reqDirParents,
    reqTypes,
    reqTypeGroups,
    reqYears,
    valType,
    pubType,
  }
  return removeUndefinedProperties(criteria)
}

function getSearchByLocationCriteriaFromParamsAndFilters({ filters, urlQueryParams }) {
  const { lat, lng, distance } = urlQueryParams
  const { reqTypes, reqTypeGroups, reqTypeLabel } = getPlaceTypeCriteriaFromFilters(filters)
  const reqYears = getDateCriteriaFromFilters(filters)
  const { valType, pubType } = getCertificationLevelCriteriaFromFilters(filters)
  const criteria = {
    lat,
    lng,
    distance,
    reqTypes,
    reqTypeGroups,
    reqTypeLabel,
    reqYears,
    valType,
    pubType,
  }
  return removeUndefinedProperties(criteria)
}

function getSearchByTextSearchCriteriaFromParamsAndFilters({ filters, urlQueryParams }) {
  const { text } = urlQueryParams
  const { reqTypes, reqTypeGroups, reqTypeLabel } = getPlaceTypeCriteriaFromFilters(filters)
  const exactFilter = getExactFilterFromFilters(filters)
  const partial = !exactFilter

  const reqYears = getDateCriteriaFromFilters(filters)
  const { valType, pubType } = getCertificationLevelCriteriaFromFilters(filters)
  const criteria = {
    text,
    reqTypes,
    reqTypeGroups,
    reqTypeLabel,
    reqYears,
    valType,
    pubType,
    partial,
  }
  return removeUndefinedProperties(criteria)
}

export function getSearchCriteriaFromParamsAndFilters({ urlQueryParams = {}, filters = [] }) {
  const { text } = urlQueryParams
  const selectedFilters = filters.filter((filter) => filter.selected)
  const searchType = getSearchTypeFromParamsAndFilters({ filters: selectedFilters, urlQueryParams })
  const commonCriteria = getCommonSearchCriteriaFromParams(urlQueryParams)
  switch (searchType) {
    case SEARCH_TYPE.SEARCH_WITHIN:
      return text
        ? { ...getSearchWithinSearchCriteriaFromFilters(selectedFilters), ...commonCriteria, text }
        : { ...getSearchWithinSearchCriteriaFromFilters(selectedFilters), ...commonCriteria }
    case SEARCH_TYPE.LOCATION:
      return text
        ? {
            ...getSearchByLocationCriteriaFromParamsAndFilters({ filters: selectedFilters, urlQueryParams, text }),
            ...commonCriteria,
          }
        : {
            ...getSearchByLocationCriteriaFromParamsAndFilters({ filters: selectedFilters, urlQueryParams }),
            ...commonCriteria,
          }
    case SEARCH_TYPE.TEXT:
    default:
      return {
        ...getSearchByTextSearchCriteriaFromParamsAndFilters({ filters: selectedFilters, urlQueryParams }),
        ...commonCriteria,
      }
  }
}

function getClientFiltersFromParamsAndFilters({ filters, urlQueryParams }) {
  const searchType = getSearchTypeFromParamsAndFilters({ filters, urlQueryParams })
  switch (searchType) {
    case SEARCH_TYPE.SEARCH_WITHIN:
      return filters
    case SEARCH_TYPE.LOCATION:
      return filters
    case SEARCH_TYPE.TEXT:
    default:
      return []
  }
}

export function getResultsClientFilterFn({ urlQueryParams, filters }) {
  const clientFilters = getClientFiltersFromParamsAndFilters({ filters, urlQueryParams })
  const activeFilters = clientFilters.filter((filter) => filter.selected)
  return getInternalResultsClientFilterFn({ filters: activeFilters, text: urlQueryParams.text })
}

/* Parse latitude and longitude to decimal values
 * Example: 36°57'9" N 110°4'21" W =>  36.9525000, -110.0725000
 * https://stackoverflow.com/questions/1140189/converting-latitude-and-longitude-to-decimal-values
 */
function parseLatLngDMS(input) {
  if (!input || !input.length) return undefined
  const parts = input.trim().split(/[^\d\w]+/)
  let lat
  let lng
  if (parts.length === 8) {
    lat = convertLatLngDMSToDecimal(parts[0], parts[1], parts[2], parts[3])
    lng = convertLatLngDMSToDecimal(parts[4], parts[5], parts[6], parts[7])
    if (lat && lng) {
      return { lat, lng }
    }
  }
  if (parts.length === 10) {
    lat = convertLatLngDMSToDecimal(parts[0], parts[1], [parts[2], parts[3]].join('.'), parts[4])
    lng = convertLatLngDMSToDecimal(parts[5], parts[6], [parts[7], parts[8]].join('.'), parts[9])
    return { lat, lng }
  }
  return undefined
}

function convertLatLngDMSToDecimal(degrees, minutes, seconds, direction) {
  const VALID_DIRECTIONS = { N: true, S: true, E: true, W: true }
  const degs = Number(degrees)
  const mins = Number(minutes)
  const secs = Number(seconds)
  if (Number.isNaN(degs) || Number.isNaN(mins) || Number.isNaN(secs) || !direction || !VALID_DIRECTIONS[direction]) {
    return undefined
  }

  let dd = degs + mins / 60 + secs / (60 * 60)

  if (direction === 'S' || direction === 'W') {
    dd *= -1
  } // Don't do anything for N or E
  return dd
}

function parseLatLngDecimal(input) {
  const placeSearchTerms = input.split(/[, ]+/)
  return placeSearchTerms.reduce((location, placeSearchTerm) => {
    const isWithin = placeSearchTerm.toUpperCase() === 'WITHIN'
    if (isWithin) {
      location.isWithin = true
      return location
    }

    if (location.isWithin && placeSearchTerm.toUpperCase() === 'MILES') {
      location.isMiles = true
      return location
    }

    if (location.isWithin && placeSearchTerm.toUpperCase() === 'KILOMETERS') {
      location.isKilometers = true
      return location
    }

    let floatTerm = getDistanceValue(placeSearchTerm)
    if (!Number.isNaN(floatTerm) && location.isWithin && !location.distance) {
      location.distance = floatTerm
      return location
    }

    if (!Number.isNaN(floatTerm) && !location.lat && (floatTerm >= -85 || floatTerm <= 85)) {
      location.lat = floatTerm
      return location
    }

    if (!Number.isNaN(floatTerm) && floatTerm >= -180 && floatTerm <= 180 && !location.lng) {
      /* eslint-disable */
      if (floatTerm == -180) floatTerm = -179.9
      if (floatTerm == 180) floatTerm = 179.9
      /* eslint-enable */
      location.lng = floatTerm
      return location
    }
    return location
  }, {})
}

function parseLocationFromTextInput(text) {
  const dmsLatLng = parseLatLngDMS(text)
  if (dmsLatLng) {
    return dmsLatLng
  }
  return parseLatLngDecimal(text)
}

/**
 * Updates the search parameters for browser location
 *
 * @param {Object} history The history object from @fs/zion-router
 * @param {Object} [addParams] The parameters and values to add to the current query.
 * @param {Object} [deleteParams] The parameters to remove from the current query.
 * @param {Object} [addHashParams] The hash parameters and values to add to the current query.
 * @param {Object} [deleteHashParams] The hash parameters to remove from the current query.
 * @param {boolean} [deleteAll] Whether to delete all parameters.
 * @param {method} = The history method to use ('push' or 'replace').
 */
export function updatePlacesUrl({
  history,
  addParams = {},
  deleteParams = {},
  addHashParams = {},
  deleteHashParams = {},
  deleteAll = false,
  method = 'push',
}) {
  const initParams = QueryString.parse(history.location.search)
  const initHashParams = QueryString.parse(window.location.hash.substr(1))
  const { exact, limit } = initParams
  const params = deleteAll ? {} : { ...initParams }
  const hashParams = deleteAll ? {} : { ...initHashParams }
  let urlChanged = deleteAll

  Object.keys(deleteParams).forEach((key) => {
    urlChanged = true
    debug(`deleteParams: key:${key}`)
    delete params[key]
  })

  Object.keys(deleteHashParams).forEach((key) => {
    urlChanged = true
    debug(`deleteHashParams: key:${key}`)
    delete hashParams[key]
  })

  Object.keys(addParams).forEach((key) => {
    const currentValue = params[key]
    const newValue = addParams[key]
    if (newValue !== currentValue) {
      debug(`addParams: key:${key}`)
      urlChanged = true
      params[key] = encodeURIComponent(newValue)
    }
  })

  Object.keys(addHashParams).forEach((key) => {
    const currentValue = hashParams[key]
    const newValue = addHashParams[key]
    if (newValue !== currentValue) {
      debug(`addHashParams: key:${key}`)
      urlChanged = true
      hashParams[key] = encodeURIComponent(newValue)
    }
  })

  if (urlChanged) {
    if (deleteAll && exact) {
      params.exact = exact
    }
    if (deleteAll && Number(limit) > 0) {
      params.limit = limit
    }

    const filters = uriDecodeCheck(hashParams.filters)
    if ((filters?.length ?? 0) < EMPTY_FILTER_LENGTH) {
      delete hashParams.filters
    }
    const newQueryString = `?${Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join('&')}`
    const newHashString =
      Object.keys(hashParams).length > 0
        ? `#${Object.keys(hashParams)
            .map((key) => `${key}=${hashParams[key]}`)
            .join('&')}`
        : ''
    debug(`updateUrl: history[${method}:${newQueryString}${newHashString}`)
    history[method](newQueryString + newHashString)
  }
}

function hasLocationParams(params) {
  return params && !Number.isNaN(params.lat) && !Number.isNaN(params.lng) && !!params.distance
}
export function hasLocationQueryParams(history) {
  // Safely handle history being null initially
  if (!history?.location?.search) {
    return false
  }
  const queryString = history.location.search
  const params = QueryString.parse(queryString)
  return hasLocationParams(params)
}

/**
 * Filters a single place option based on search criteria
 * @param {Object} params - Filter parameters
 * @param {Object} params.placeOption - The place option to filter
 * @param {Object} params.criteria - Search criteria from URL parameters
 * @returns {boolean} - True if the place option passes all filters
 */
export function filterSinglePlaceOption({ placeOption, criteria }) {
  if (!placeOption) return false

  const { place } = placeOption

  // Filter by place type
  if (criteria.reqTypes) {
    const typeIds = criteria.reqTypes.split(',').map(Number)
    if (!typeIds.includes(place.placeType?.id)) {
      return false
    }
  }

  // Filter by date range
  if (criteria.reqYears) {
    const dateRanges = criteria.reqYears.split(',')

    const matchesDateRange = dateRanges.some((range) => {
      const [fromYear, toYear] = range.split('-').map(Number)
      const placeFrom = place.dateRange?.from || 0
      const placeTo = place.dateRange?.to || new Date().getFullYear()

      // Check if the place's date range overlaps with the criteria date range
      return (placeFrom <= toYear || !toYear) && (placeTo >= fromYear || !fromYear)
    })

    if (!matchesDateRange) {
      return false
    }
  }

  // Filter by certification level
  if (criteria.valType || criteria.pubType) {
    // Map certainty values to validation and publication status
    // certainty: 0 = provisional, 1 = accepted, 2 = certified
    const certainty = place.certainty || 0

    if (criteria.valType === 'val_only' && certainty < 2) {
      return false
    }

    if (criteria.valType === 'non_val_only' && certainty >= 2) {
      return false
    }

    if (criteria.pubType === 'pub_only' && certainty < 1) {
      return false
    }

    if (criteria.pubType === 'non_pub_only' && certainty >= 1) {
      return false
    }

    if (criteria.certificationLevel === 'not-accepted' && certainty === 1) {
      return false
    }
  }

  // Filter by parent place IDs
  if (criteria.reqParents) {
    const parentIds = criteria.reqParents.split(',').map(Number)
    const isInJurisdictionChain = place.jurisdictionChain?.some((id) => parentIds.includes(id))
    const isExactPlace = parentIds.includes(place.id)

    if (!isInJurisdictionChain && !isExactPlace) {
      return false
    }
  }

  // Filter by direct parent place ID
  if (criteria.reqDirParents) {
    const directParentIds = criteria.reqDirParents.split(',').map(Number)

    // For direct parent, check if it's the first ID in the jurisdiction chain
    // or if jurisdictionChain isn't available, check if it matches the parentPlace.id
    const directParentId = place.jurisdictionChain?.[0] || place.parentPlace?.id

    if (!directParentIds.includes(directParentId)) {
      return false
    }
  }

  // Handle directChildrenOnly flag
  if (criteria.directChildrenOnly === 'true' && criteria.reqParents) {
    const parentIds = criteria.reqParents.split(',').map(Number)
    const directParentId = place.jurisdictionChain?.[0] || place.parentPlace?.id

    if (!parentIds.includes(directParentId)) {
      return false
    }
  }

  return true
}
